import jsPDF from "jspdf";
import "jspdf-autotable";
import { DocProps } from "../types";
import { hexToGrayscale, pxToInch } from "./helpers";

var generateData = function (tableData) {
  const rows = tableData.rows?.map((row: any) => {
    let object = {};
    tableData?.columns?.forEach((col: any) => {
      const content =
        row?.column_content.find(
          (content: any) => content?.field === col?.field
        )?.content || "";
      object[col.field] = content;
    });
    return object;
  });
  return rows;
};

const addHeaderTitle = (doc, title) => {
  if (title) {
    // Text position and dimensions
    const x = 7;
    const y = 12;
    const padding = 2;
    const fontSize = 14;

    // Set the font size to calculate the width and height of the text
    doc.setFontSize(fontSize);
    const textWidth = doc.getTextWidth(title);
    const textHeight = fontSize / 2;

    // Draw background rectangle
    doc.setFillColor(194, 159, 255); // Background color
    doc.rect(
      x - padding / 2,
      y - textHeight,
      textWidth + padding * 2,
      textHeight + padding * 2,
      "F"
    );

    // Draw text
    doc.setTextColor(0, 0, 0); // Text color
    doc.text(title, x, y);
  }
};

const drawTable = (doc, tableData, startY) => {
  const totalWidth = tableData.columns.reduce(
    (sum, col) => sum + (col.width || 150),
    0
  );

  let tableHeight = 0;

  // Text position and dimensions
  const x = 7;
  const titleFontSize = 12;
  const descFontSize = 10;

  // Draw title
  doc.setFontSize(titleFontSize);
  doc.setTextColor(0, 0, 0); // Text color
  doc.text(tableData.name, x, startY);
  const titleWidth = doc.getTextWidth(tableData.name);
  const titleHeight = titleFontSize / 2;

  // Draw description if available
  let descHeight = 0;
  if (tableData.description) {
    doc.setFontSize(descFontSize);
    doc.setTextColor(128, 128, 128); // Text color
    doc.text(`(${tableData.description})`, titleWidth + x + 2, startY);
    descHeight = descFontSize / 2;
  }

  // Calculate the starting Y position for the table
  const tableStartY = startY + Math.max(titleHeight, descHeight);

  doc.autoTable({
    startY: tableStartY,
    head: [tableData.columns.map((col) => col.header_name)],
    body: generateData(tableData),
    columns: tableData.columns.map((col) => ({
      dataKey: col.field,
      header: col.header_name,
      //   width: `${pxToInch(col.width || 150)}pt`
    })),
    // columnDefs: tableData.columns.map((col) => ({
    //   targets: col.field,
    //   width: pxToInch(col.width || 150),
    // })), 
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: tableData.header_color,
      textColor: `${
        hexToGrayscale(tableData.header_color) > 150 ? "#000000" : "#FFFFFF"
      }`,
    },
    columnStyles: tableData.columns.reduce((acc, col, index) => {
      acc[col.field] = { cellWidth: pxToInch(col.width || 150) };
      return acc;
    }, {}),

    didDrawCell: (data) => {
      if (data.cell.section === "body" || data.cell.section === "head") {
        const { doc } = data;
        doc.setDrawColor(128, 128, 128); // Set border color to grey (RGB: 128, 128, 128)
        // doc.rect(cell.x, cell.y, cell.width, cell.height, "S");
      }
    },
    didDrawPage: (data) => {
      // This will be called after each page is drawn
      tableHeight = data.cursor.y - tableStartY;
    },
    tableWidth: pxToInch(totalWidth),
    margin: { left: x, right: x },
  });

  // Calculate and return the total height (text + table)
  const totalHeight = tableHeight + Math.max(titleHeight, descHeight) + 5;
  return startY + totalHeight;
};

export const generateDataSheet = ({
  title,
  dataSheet,
  basicDetails,
  ...rest
}: DocProps) => {

  const basicData = {
    header_color: "#d1c4e9",
    columns: [
      { header_name: "S.No", field: "s-no", width: 45 },
      { header_name: "Title", field: "title", width: 280 },
      { header_name: "Details", field: "details", width: 410 },
    ],
    rows: [
      ...(basicDetails?.optionDetails ?? [])?.map((mat, idx: number) => {
        return {
          column_content: [
            { content: (idx + 1).toString(), field: "s-no" },
            { content: mat?.label ?? "", field: "title" },
            { content: mat?.value ?? "", field: "details" },
          ],
          id: idx + 1,
        };
      }),
      ...(basicDetails?.attribute_details?.map((mat, idx: number) => {
        return {
          column_content: [
            {
              content: (basicDetails?.optionDetails?.length ?? 0) + idx + 1,
              field: "s-no",
            },
            { content: mat?.attr_name ?? "", field: "title" },
            { content: mat?.option_abbr ?? "", field: "details" },
          ],
          id: (basicDetails?.optionDetails?.length ?? 0) + idx + 1,
        };
      }) ?? []),
      ...(basicDetails?.notes
        ? [
          {
            column_content: [
              {
                content:
                  (basicDetails?.optionDetails?.length ?? 0) +
                  (basicDetails?.attribute_details?.length ?? 0) +
                  1,
                field: "s-no",
              },
              { content: "Notes", field: "title" },
              { content: basicDetails?.notes ?? "", field: "details" },
            ],
            id:
              (basicDetails?.optionDetails?.length ?? 0) +
              (basicDetails?.attribute_details?.length ?? 0) +
              1,
          },
        ]
        : []),
    ],
    name: "BASIC DETAILS",
  };

  let doc = new jsPDF({
    putOnlyUsedFonts: true,
    orientation: rest.orientation ?? "portrait",
    format: rest.paperSize,
  });

  // Set document properties
  doc.setProperties({ title: title || dataSheet?.sequence_id || dataSheet?.name });

  if (rest.headerTitle) {
    addHeaderTitle(doc, rest.headerTitle);

    // Add a page event listener to add the header title on new pages
    // doc.internal.events.subscribe('addPage', () => addHeaderTitle(doc, rest.headerTitle));
  }

  let currentY = rest.headerTitle ? 25 : 0;
  if (basicDetails) {
    currentY = drawTable(doc, basicData, currentY);
  }

  dataSheet?.tables?.forEach((table: any, idx: number) => {
    const updatedTable = {
      ...table,
      columns:
        table?.columns?.length > 0
          ? [
            {
              header_name: "S.No",
              field: "id",
              width: 45,
              header_color: table?.header_color,
            },
            ...table?.columns,
          ]
          : [],
      rows: table?.rows?.map((row: any, idx: number) => {
        return {
          column_content: [
            { content: idx + 1, field: "id" },
            ...row?.column_content,
          ],
          id: idx + 1,
        };
      }),
      description: table?.description ?? ""
    };

    // Add some space between tables
    currentY += 10;

    // Check if we need to add a new page
    if (currentY > doc.internal.pageSize.height - 20) {
      doc.addPage();
      currentY = 20; // Reset Y position for the new page
    }

    currentY = drawTable(doc, updatedTable, currentY);
  });

  return doc.output("datauristring"); // base64 string
};
